import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Mail, RefreshCw, Users, CheckCircle2, Search, Plus } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  SERVICE_INTERESTS,
  COMPANY_SIZES,
  PACKAGE_INTERESTS,
  QUERY_TOPICS,
  CONTACT_REASONS,
  TIMING_OPTIONS,
  LEAD_SOURCES,
  PREFERRED_CONTACT,
} from "@/data/content";

const CRM_SERVICE_INTERESTS = [
  ...new Set([...SERVICE_INTERESTS, ...QUERY_TOPICS]),
];

const MANUAL_ENQUIRY_TYPES = [
  "Manual Opportunity",
  "Partner Referral",
  "Phone Enquiry",
  "Networking / Event",
  "Existing Contact",
  "Inbound Referral",
  "Other",
];

const MANUAL_LEAD_SOURCES = [
  ...LEAD_SOURCES,
  "Telephone",
  "Email",
];

const PIPELINE_STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "discovery_booked", label: "Discovery Booked" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];


function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-brandborder rounded-md p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wide uppercase text-slate">{label}</span>
        <Icon size={18} className="text-brandblue" />
      </div>
      <div className="mt-3 font-heading font-extrabold text-3xl text-navy">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const canEdit = user?.role !== "read_only";
  const canExport = ["super_admin", "admin"].includes(user?.role);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completingAction, setCompletingAction] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [followUpFilter, setFollowUpFilter] = useState("all_leads");
  const [proposalStatusFilter, setProposalStatusFilter] = useState("all");
  const [projectStatusFilter, setProjectStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [rayConversation, setRayConversation] = useState([]);
  const [loadingRayConversation, setLoadingRayConversation] = useState(false);
  const [rayConversationError, setRayConversationError] = useState("");

  const [proposals, setProposals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingCommercial, setLoadingCommercial] = useState(false);

  const [proposalForm, setProposalForm] = useState({
    title: "",
    value: "",
    notes: "",
  });
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [updatingProposalId, setUpdatingProposalId] = useState("");
  const [updatingProjectId, setUpdatingProjectId] = useState("");

  const [invoiceForm, setInvoiceForm] = useState({
    amount: "",
    status: "draft",
    external_reference: "",
    notes: "",
  });
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [updatingInvoiceId, setUpdatingInvoiceId] = useState("");

  const [proposalActivity, setProposalActivity] = useState({});
  const [projectActivity, setProjectActivity] = useState({});
  const [invoiceActivity, setInvoiceActivity] = useState({});
  const [savingActivityKey, setSavingActivityKey] = useState("");

  const [showNewOpportunity, setShowNewOpportunity] = useState(false);
  const [creatingOpportunity, setCreatingOpportunity] = useState(false);
  const [newOpportunity, setNewOpportunity] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    job_title: "",
    company_size: "",
    lead_source: "",
    enquiry_type: "Manual Opportunity",
    service_interest: "",
    package_interest: "",
    contact_reason: "",
    preferred_contact: "",
    message: "",
  });

  const resetNewOpportunity = () =>
    setNewOpportunity({
      name: "",
      company: "",
      email: "",
      phone: "",
      job_title: "",
      company_size: "",
      lead_source: "",
      enquiry_type: "Manual Opportunity",
      service_interest: "",
      package_interest: "",
      contact_reason: "",
      preferred_contact: "",
      message: "",
    });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "tworay-leads.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const loadRayConversation = async (lead) => {
    setRayConversation([]);
    setRayConversationError("");

    if (!lead.session_id) {
      setLoadingRayConversation(false);
      return;
    }

    setLoadingRayConversation(true);

    try {
      const { data } = await api.get(
        `/chat/sessions/${encodeURIComponent(lead.session_id)}`
      );
      setRayConversation(data.messages || []);
    } catch {
      setRayConversationError("Unable to load the Ray conversation.");
    } finally {
      setLoadingRayConversation(false);
    }
  };

  const loadCommercialRecords = async (lead) => {
    setProposals([]);
    setProjects([]);
    setInvoices([]);

    if (!lead?.id) return;

    setLoadingCommercial(true);

    try {
      const [proposalResponse, projectResponse] = await Promise.all([
        api.get(`/leads/${lead.id}/proposals`),
        api.get(`/leads/${lead.id}/projects`),
      ]);

      const loadedProposals = proposalResponse.data || [];
      const loadedProjects = projectResponse.data || [];

      setProposals(loadedProposals);
      setProjects(loadedProjects);

      if (loadedProjects.length > 0) {
        const invoiceResponses = await Promise.all(
          loadedProjects.map((project) =>
            api.get(`/projects/${project.id}/invoices`)
          )
        );

        setInvoices(
          invoiceResponses.flatMap((response) => response.data || [])
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Unable to load commercial records."
      );
    } finally {
      setLoadingCommercial(false);
    }
  };

  const openLead = async (lead) => {
    setSelected(lead);

    if (canEdit) {
      try {
        const { data } = await api.post(`/leads/${lead.id}/viewed`);

        const viewedAt = data.last_viewed_at;

        setSelected((current) =>
          current?.id === lead.id
            ? {
                ...current,
                last_viewed_at: viewedAt,
              }
            : current
        );

        setLeads((items) =>
          items.map((item) =>
            item.id === lead.id
              ? {
                  ...item,
                  last_viewed_at: viewedAt,
                }
              : item
          )
        );
      } catch (error) {
        console.error("Failed to mark CRM record as viewed", error);
      }
    }
    setNewNote("");
    setProposalForm({
      title: "",
      value: "",
      notes: "",
    });
    setInvoiceForm({
      amount: "",
      status: "draft",
      external_reference: "",
      notes: "",
    });
    loadRayConversation(lead);
    loadCommercialRecords(lead);
    setEditing({
      status: lead.status || "new",
      company: lead.company || "",
      job_title: lead.job_title || "",
      company_size: lead.company_size || "",
      phone: lead.phone || "",
      address: lead.address || "",
      service_interest: lead.service_interest || "",
      package_interest: lead.package_interest || "",
      preferred_contact: lead.preferred_contact || "",
      contact_reason: lead.contact_reason || "",
      timing: lead.timing || "",
      lead_source: lead.lead_source || "",
      lead_channel: lead.lead_channel || "",
      enquiry_type: lead.enquiry_type || "",
      prompted: lead.prompted || "",
      concern: lead.concern || "",
      success_outcome: lead.success_outcome || "",
      deadline: lead.deadline || "",
      estimated_value: lead.estimated_value ?? "",
      next_action: lead.next_action || "",
      next_action_at: lead.next_action_at
        ? new Date(lead.next_action_at)
            .toLocaleString("sv-SE")
            .slice(0, 16)
        : "",
      lost_reason: lead.lost_reason || "",
    });
  };

  const saveLead = async () => {
    if (!canEdit) return;
    if (!selected || !editing) return;

    if (
      editing.status === "lost" &&
      selected.status !== "lost" &&
      !editing.lost_reason.trim()
    ) {
      toast.error("Please enter a lost reason before closing this opportunity.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...editing,
        estimated_value:
          editing.estimated_value === ""
            ? null
            : Number(editing.estimated_value),
        next_action_at:
          editing.next_action_at === ""
            ? null
            : new Date(editing.next_action_at).toISOString(),
        lost_reason:
          editing.status === "lost"
            ? editing.lost_reason
            : "",
      };

      const { data } = await api.patch(`/leads/${selected.id}`, payload);

      setLeads((items) =>
        items.map((lead) => (lead.id === data.id ? data : lead))
      );
      setSelected(data);
      await loadCommercialRecords(data);
      setEditing({
        status: data.status || "new",
        company: data.company || "",
        job_title: data.job_title || "",
        company_size: data.company_size || "",
        phone: data.phone || "",
        address: data.address || "",
        service_interest: data.service_interest || "",
        package_interest: data.package_interest || "",
        preferred_contact: data.preferred_contact || "",
        contact_reason: data.contact_reason || "",
        timing: data.timing || "",
        lead_source: data.lead_source || "",
        lead_channel: data.lead_channel || "",
        enquiry_type: data.enquiry_type || "",
        prompted: data.prompted || "",
        concern: data.concern || "",
        success_outcome: data.success_outcome || "",
        deadline: data.deadline || "",
        estimated_value: data.estimated_value ?? "",
        next_action: data.next_action || "",
        next_action_at: data.next_action_at
          ? new Date(data.next_action_at)
              .toLocaleString("sv-SE")
              .slice(0, 16)
          : "",
        lost_reason: data.lost_reason || "",
      });

      toast.success("Lead updated.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update lead.");
    } finally {
      setSaving(false);
    }
  };

  const completeNextAction = async () => {
    if (!canEdit) return;
    if (!selected) return;

    setCompletingAction(true);
    try {
      const { data } = await api.patch(`/leads/${selected.id}`, {
        next_action: "",
        next_action_at: null,
      });

      setLeads((items) =>
        items.map((lead) => (lead.id === data.id ? data : lead))
      );

      setSelected(data);
      setEditing((current) =>
        current
          ? {
              ...current,
              next_action: "",
              next_action_at: "",
            }
          : current
      );

      toast.success("Next action completed.");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to complete next action."
      );
    } finally {
      setCompletingAction(false);
    }
  };

  const addNote = async () => {
    if (!canEdit) return;
    if (!selected || !newNote.trim()) return;

    setSavingNote(true);
    try {
      const { data } = await api.post(`/leads/${selected.id}/notes`, {
        note: newNote.trim(),
      });

      setLeads((items) =>
        items.map((lead) => (lead.id === data.id ? data : lead))
      );
      setSelected(data);
      setNewNote("");
      toast.success("Note added.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to add note.");
    } finally {
      setSavingNote(false);
    }
  };

  const createProposal = async () => {
    if (!canEdit) return;
    if (!selected) return;

    setCreatingProposal(true);

    try {
      const payload = {
        title: proposalForm.title.trim(),
        value:
          proposalForm.value === ""
            ? null
            : Number(proposalForm.value),
        notes: proposalForm.notes.trim(),
      };

      const { data } = await api.post(
        `/leads/${selected.id}/proposals`,
        payload
      );

      setProposals((items) => [data, ...items]);

      setSelected((current) =>
        current
          ? {
              ...current,
              latest_proposal_ref: data.proposal_ref,
              proposal_refs: [
                ...new Set([
                  ...(current.proposal_refs || []),
                  data.proposal_ref,
                ]),
              ],
            }
          : current
      );

      setLeads((items) =>
        items.map((lead) =>
          lead.id === selected.id
            ? {
                ...lead,
                latest_proposal_ref: data.proposal_ref,
                proposal_refs: [
                  ...new Set([
                    ...(lead.proposal_refs || []),
                    data.proposal_ref,
                  ]),
                ],
              }
            : lead
        )
      );

      setProposalForm({
        title: "",
        value: "",
        notes: "",
      });

      toast.success(`Proposal ${data.proposal_ref} created.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to create proposal."
      );
    } finally {
      setCreatingProposal(false);
    }
  };

  const updateProposalStatus = async (proposal, status) => {
    if (!canEdit) return;
    if (!proposal?.id || !status) return;

    setUpdatingProposalId(proposal.id);

    try {
      const { data } = await api.patch(
        `/proposals/${proposal.id}`,
        { status }
      );

      setProposals((items) =>
        items.map((item) =>
          item.id === data.id ? data : item
        )
      );

      if (selected?.latest_proposal_ref === data.proposal_ref) {
        setSelected((current) =>
          current
            ? {
                ...current,
                latest_proposal_status: data.status,
              }
            : current
        );

        setLeads((items) =>
          items.map((lead) =>
            lead.id === selected.id
              ? {
                  ...lead,
                  latest_proposal_status: data.status,
                }
              : lead
          )
        );
      }

      toast.success(
        `Proposal ${data.proposal_ref} updated to ${data.status}.`
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to update proposal."
      );
    } finally {
      setUpdatingProposalId("");
    }
  };

  const updateProjectStatus = async (project, status) => {
    if (!canEdit) return;
    if (!project?.id || !status) return;

    setUpdatingProjectId(project.id);

    try {
      const { data } = await api.patch(
        `/projects/${project.id}`,
        { status }
      );

      setProjects((items) =>
        items.map((item) =>
          item.id === data.id ? data : item
        )
      );

      setSelected((current) =>
        current
          ? {
              ...current,
              project_status: data.status,
            }
          : current
      );

      setLeads((items) =>
        items.map((lead) =>
          lead.id === selected.id
            ? {
                ...lead,
                project_status: data.status,
              }
            : lead
        )
      );

      toast.success(
        `Project ${data.project_ref} updated to ${data.status.replace("_", " ")}.`
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to update project."
      );
    } finally {
      setUpdatingProjectId("");
    }
  };

  const createInvoice = async (project) => {
    if (!canEdit) return;
    if (!selected || !project) return;

    setCreatingInvoice(true);

    try {
      const payload = {
        amount:
          invoiceForm.amount === ""
            ? null
            : Number(invoiceForm.amount),
        status: invoiceForm.status || "draft",
        external_reference: invoiceForm.external_reference.trim(),
        notes: invoiceForm.notes.trim(),
      };

      const { data } = await api.post(
        `/projects/${project.id}/invoices`,
        payload
      );

      setInvoices((items) => [data, ...items]);

      setSelected((current) =>
        current
          ? {
              ...current,
              latest_invoice_ref: data.invoice_ref,
              invoice_refs: [
                ...new Set([
                  ...(current.invoice_refs || []),
                  data.invoice_ref,
                ]),
              ],
            }
          : current
      );

      setLeads((items) =>
        items.map((lead) =>
          lead.id === selected.id
            ? {
                ...lead,
                latest_invoice_ref: data.invoice_ref,
                invoice_refs: [
                  ...new Set([
                    ...(lead.invoice_refs || []),
                    data.invoice_ref,
                  ]),
                ],
              }
            : lead
        )
      );

      setInvoiceForm({
        amount: "",
        status: "draft",
        external_reference: "",
        notes: "",
      });

      toast.success(`Invoice ${data.invoice_ref} recorded.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to record invoice."
      );
    } finally {
      setCreatingInvoice(false);
    }
  };

  const updateInvoiceStatus = async (invoice, status) => {
    if (!canEdit) return;
    if (!invoice?.id || !status) return;

    setUpdatingInvoiceId(invoice.id);

    try {
      const { data } = await api.patch(
        `/invoices/${invoice.id}`,
        { status }
      );

      setInvoices((items) =>
        items.map((item) =>
          item.id === data.id ? data : item
        )
      );

      toast.success(
        `Invoice ${data.invoice_ref} updated to ${data.status}.`
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to update invoice."
      );
    } finally {
      setUpdatingInvoiceId("");
    }
  };

  const addProposalActivity = async (proposal) => {
    if (!canEdit) return;
    const note = (proposalActivity[proposal.id] || "").trim();
    if (!note) return;

    const key = `proposal-${proposal.id}`;
    setSavingActivityKey(key);

    try {
      const { data } = await api.post(
        `/proposals/${proposal.id}/activity`,
        { note }
      );

      setProposals((items) =>
        items.map((item) =>
          item.id === data.id ? data : item
        )
      );

      setProposalActivity((current) => ({
        ...current,
        [proposal.id]: "",
      }));

      toast.success(`Activity added to ${data.proposal_ref}.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to add proposal activity."
      );
    } finally {
      setSavingActivityKey("");
    }
  };

  const addProjectActivity = async (project) => {
    if (!canEdit) return;
    const note = (projectActivity[project.id] || "").trim();
    if (!note) return;

    const key = `project-${project.id}`;
    setSavingActivityKey(key);

    try {
      const { data } = await api.post(
        `/projects/${project.id}/activity`,
        { note }
      );

      setProjects((items) =>
        items.map((item) =>
          item.id === data.id ? data : item
        )
      );

      setProjectActivity((current) => ({
        ...current,
        [project.id]: "",
      }));

      toast.success(`Activity added to ${data.project_ref}.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to add project activity."
      );
    } finally {
      setSavingActivityKey("");
    }
  };

  const addInvoiceActivity = async (invoice) => {
    if (!canEdit) return;
    const note = (invoiceActivity[invoice.id] || "").trim();
    if (!note) return;

    const key = `invoice-${invoice.id}`;
    setSavingActivityKey(key);

    try {
      const { data } = await api.post(
        `/invoices/${invoice.id}/activity`,
        { note }
      );

      setInvoices((items) =>
        items.map((item) =>
          item.id === data.id ? data : item
        )
      );

      setInvoiceActivity((current) => ({
        ...current,
        [invoice.id]: "",
      }));

      toast.success(`Activity added to ${data.invoice_ref}.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to add invoice activity."
      );
    } finally {
      setSavingActivityKey("");
    }
  };

  const createOpportunity = async () => {
    if (!canEdit) return;
    if (!newOpportunity.name.trim()) {
      toast.error("Please enter a contact name.");
      return;
    }

    if (!newOpportunity.email.trim()) {
      toast.error("Please enter an email address.");
      return;
    }

    setCreatingOpportunity(true);

    try {
      const payload = {
        ...newOpportunity,
        name: newOpportunity.name.trim(),
        company: newOpportunity.company.trim(),
        email: newOpportunity.email.trim(),
        phone: newOpportunity.phone.trim(),
        job_title: newOpportunity.job_title.trim(),
        lead_channel: "Admin / Manual",
        source: "admin",
        privacy_ack: false,
        marketing_opt_in: false,
      };

      const { data } = await api.post("/admin/leads", payload);

      setLeads((items) => [data, ...items]);
      setShowNewOpportunity(false);
      resetNewOpportunity();

      toast.success(`Opportunity ${data.master_ref} created.`);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Failed to create opportunity."
      );
    } finally {
      setCreatingOpportunity(false);
    }
  };

  const del = async (id) => {
    if (!canEdit) return;
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const openPipeline = leads.filter(
    (l) => !["won", "lost"].includes(l.status || "new")
  );

  const pipelineValue = openPipeline.reduce(
    (sum, lead) => sum + (Number(lead.estimated_value) || 0),
    0
  );

  const formatCurrency = (value) =>
    `\u00a3${Number(value || 0).toLocaleString("en-GB")}`;

  const valueForStatus = (status) =>
    leads
      .filter((lead) => (lead.status || "new") === status)
      .reduce(
        (sum, lead) => sum + (Number(lead.estimated_value) || 0),
        0
      );

  const proposalValue = valueForStatus("proposal_sent");
  const wonValue = valueForStatus("won");
  const lostValue = valueForStatus("lost");

  const wonCount = leads.filter(
    (lead) => (lead.status || "new") === "won"
  ).length;

  const lostCount = leads.filter(
    (lead) => (lead.status || "new") === "lost"
  ).length;

  const closedCount = wonCount + lostCount;
  const winRate =
    closedCount === 0
      ? 0
      : Math.round((wonCount / closedCount) * 100);

  const hasNewEnquiryUpdate = (lead) => {
    const history = lead.enquiry_history || [];

    if (history.length === 0) return false;

    const latestEventTime = Math.max(
      ...history
        .map((event) => new Date(event.created_at).getTime())
        .filter((value) => Number.isFinite(value))
    );

    if (!Number.isFinite(latestEventTime)) return false;

    const baseline = new Date(
      lead.last_viewed_at || lead.created_at
    ).getTime();

    if (!Number.isFinite(baseline)) return false;

    return latestEventTime > baseline;
  };


  const getCurrentStageStartedAt = (lead) => {
    const currentStatus = lead.status || "new";
    const history = lead.status_history || [];

    for (let index = history.length - 1; index >= 0; index -= 1) {
      if (history[index].to_status === currentStatus) {
        return history[index].changed_at;
      }
    }

    return lead.created_at;
  };

  const getStageAgeDays = (lead) => {
    const startedAt = getCurrentStageStartedAt(lead);
    if (!startedAt) return 0;

    const started = new Date(startedAt);
    if (Number.isNaN(started.getTime())) return 0;

    return Math.max(
      0,
      (Date.now() - started.getTime()) / (1000 * 60 * 60 * 24)
    );
  };

  const stageReporting = PIPELINE_STATUSES.map((status) => {
    const stageLeads = leads.filter(
      (lead) => (lead.status || "new") === status.value
    );

    const value = stageLeads.reduce(
      (sum, lead) => sum + (Number(lead.estimated_value) || 0),
      0
    );

    const averageAge =
      stageLeads.length === 0
        ? 0
        : stageLeads.reduce(
            (sum, lead) => sum + getStageAgeDays(lead),
            0
          ) / stageLeads.length;

    return {
      ...status,
      count: stageLeads.length,
      totalValue: value,
      averageAge: Math.round(averageAge),
    };
  });

  const activeLeadAverageAge =
    openPipeline.length === 0
      ? 0
      : Math.round(
          openPipeline.reduce(
            (sum, lead) => sum + getStageAgeDays(lead),
            0
          ) / openPipeline.length
        );

  const getFollowUpState = (lead) => {
    if (["won", "lost"].includes(lead.status || "new")) return "none";
    if (!lead.next_action_at) return "none";

    const due = new Date(lead.next_action_at);
    if (Number.isNaN(due.getTime())) return "none";

    const now = new Date();
    const isToday =
      due.getFullYear() === now.getFullYear() &&
      due.getMonth() === now.getMonth() &&
      due.getDate() === now.getDate();

    if (due.getTime() < now.getTime()) return "overdue";
    if (isToday) return "due_today";

    return "upcoming";
  };

  const overdueFollowUps = openPipeline.filter(
    (lead) => getFollowUpState(lead) === "overdue"
  ).length;

  const dueTodayFollowUps = openPipeline.filter(
    (lead) => getFollowUpState(lead) === "due_today"
  ).length;

  const openFollowUps = openPipeline.length;

  const noActionSet = openPipeline.filter(
    (lead) => !lead.next_action && !lead.next_action_at
  ).length;

  const statusFilteredLeads =
    statusFilter === "all"
      ? leads
      : leads.filter((lead) => (lead.status || "new") === statusFilter);

  const leadChannels = [
    ...new Set(
      leads
        .map((lead) => lead.lead_channel || "Unclassified")
        .filter(Boolean)
    ),
  ].sort();

  const channelFilteredLeads =
    channelFilter === "all"
      ? statusFilteredLeads
      : statusFilteredLeads.filter(
          (lead) =>
            (lead.lead_channel || "Unclassified") === channelFilter
        );

  const followUpFilteredLeads = channelFilteredLeads.filter((lead) => {
    if (followUpFilter === "all_leads") return true;

    if (followUpFilter === "open_followups") {
      return !["won", "lost"].includes(lead.status || "new");
    }

    if (followUpFilter === "no_action") {
      return (
        !["won", "lost"].includes(lead.status || "new") &&
        !lead.next_action &&
        !lead.next_action_at
      );
    }

    return getFollowUpState(lead) === followUpFilter;
  });

  const proposalFilteredLeads = followUpFilteredLeads.filter((lead) => {
    if (proposalStatusFilter === "all") return true;

    if (proposalStatusFilter === "none") {
      return !lead.latest_proposal_status;
    }

    return lead.latest_proposal_status === proposalStatusFilter;
  });

  const projectFilteredLeads = proposalFilteredLeads.filter((lead) => {
    if (projectStatusFilter === "all") return true;

    if (projectStatusFilter === "none") {
      return !lead.project_status;
    }

    return lead.project_status === projectStatusFilter;
  });

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredLeads = projectFilteredLeads.filter((lead) => {
    if (!normalizedSearch) return true;

    const enquiryHistoryText = (lead.enquiry_history || [])
      .map((event) =>
        [
          event.enquiry_type,
          event.lead_channel,
          event.lead_source,
          event.service_interest,
          event.package_interest,
          event.contact_reason,
          event.message,
          event.company,
          event.email,
          event.phone,
        ]
          .filter(Boolean)
          .join(" ")
      )
      .join(" ");

    const searchableText = [
      lead.master_ref,
      lead.latest_proposal_ref,
      ...(lead.proposal_refs || []),
      lead.project_ref,
      lead.latest_invoice_ref,
      ...(lead.invoice_refs || []),
      lead.name,
      lead.company,
      lead.email,
      lead.phone,
      lead.service_interest,
      lead.package_interest,
      lead.contact_reason,
      lead.lead_source,
      lead.lead_channel,
      lead.enquiry_type,
      enquiryHistoryText,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSearch);
  });

  return (
    <div data-testid="page-admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-navy">Leads</h1>
          <p className="text-slate text-sm mt-1">Discovery call enquiries, newest first.</p>
        </div>
        <div className="flex gap-3">
          {canEdit && (
          <button
            onClick={() => setShowNewOpportunity(true)}
            data-testid="admin-new-opportunity-btn"
            className="inline-flex items-center gap-2 bg-brandblue text-white font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-navy transition-colors"
          >
            <Plus size={16} /> New Opportunity
          </button>
          )}
          <button onClick={load} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 border border-brandborder text-navy font-semibold text-sm px-4 py-2.5 rounded-sm hover:border-brandblue transition-colors">
            <RefreshCw size={16} /> Refresh
          </button>
          {canExport && (
          <button onClick={exportCsv} data-testid="admin-export-csv-btn" className="inline-flex items-center gap-2 bg-navy text-white font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-brandblue transition-colors">
            <Download size={16} /> Export CSV
          </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-8">
        <StatCard label="Total leads" value={leads.length} icon={Users} />
        <StatCard
          label="New leads"
          value={leads.filter((l) => (l.status || "new") === "new").length}
          icon={Mail}
        />
        <StatCard
          label="Pipeline value"
          value={`£${pipelineValue.toLocaleString("en-GB")}`}
          icon={CheckCircle2}
        />
        <StatCard
          label="Overdue follow-ups"
          value={overdueFollowUps}
          icon={CheckCircle2}
        />
      </div>

      <div className="mb-8">
        <div className="flex items-end justify-between gap-4 mb-4">
          <div>
            <h2 className="font-heading font-bold text-xl text-navy">
              CRM reporting
            </h2>
            <p className="text-sm text-slate mt-1">
              Commercial performance and pipeline ageing.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5 mb-4">
          <StatCard
            label="Proposal value"
            value={formatCurrency(proposalValue)}
            icon={CheckCircle2}
          />
          <StatCard
            label="Won value"
            value={formatCurrency(wonValue)}
            icon={CheckCircle2}
          />
          <StatCard
            label="Lost value"
            value={formatCurrency(lostValue)}
            icon={CheckCircle2}
          />
          <StatCard
            label="Win rate"
            value={`${winRate}%`}
            icon={CheckCircle2}
          />
          <StatCard
            label="Avg active stage age"
            value={`${activeLeadAverageAge}d`}
            icon={CheckCircle2}
          />
        </div>

        <div className="bg-white border border-brandborder rounded-md overflow-hidden">
          <div className="px-5 py-4 border-b border-brandborder">
            <h3 className="font-heading font-semibold text-navy">
              Pipeline by stage
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-lightbg text-slate text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">
                    Stage
                  </th>
                  <th className="text-right px-5 py-3 font-semibold">
                    Leads
                  </th>
                  <th className="text-right px-5 py-3 font-semibold">
                    Value
                  </th>
                  <th className="text-right px-5 py-3 font-semibold">
                    Avg days in stage
                  </th>
                </tr>
              </thead>
              <tbody>
                {stageReporting.map((stage) => (
                  <tr
                    key={stage.value}
                    className="border-t border-brandborder"
                  >
                    <td className="px-5 py-3 font-medium text-navy">
                      {stage.label}
                    </td>
                    <td className="px-5 py-3 text-right text-slate">
                      {stage.count}
                    </td>
                    <td className="px-5 py-3 text-right text-slate">
                      {formatCurrency(stage.totalValue)}
                    </td>
                    <td className="px-5 py-3 text-right text-slate">
                      {stage.averageAge}d
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => setStatusFilter("all")}
          className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
            statusFilter === "all"
              ? "bg-navy text-white border-navy"
              : "bg-white text-slate border-brandborder hover:text-navy"
          }`}
        >
          All
        </button>

        {PIPELINE_STATUSES.map((status) => (
          <button
            key={status.value}
            type="button"
            onClick={() => setStatusFilter(status.value)}
            className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
              statusFilter === status.value
                ? "bg-navy text-white border-navy"
                : "bg-white text-slate border-brandborder hover:text-navy"
            }`}
          >
            {status.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate mr-1">
          Lead channel
        </span>

        <button
          type="button"
          onClick={() => setChannelFilter("all")}
          className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
            channelFilter === "all"
              ? "bg-brandblue text-white border-brandblue"
              : "bg-white text-slate border-brandborder hover:text-navy"
          }`}
        >
          All channels
        </button>

        {leadChannels.map((channel) => (
          <button
            key={channel}
            type="button"
            onClick={() => setChannelFilter(channel)}
            className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
              channelFilter === channel
                ? "bg-brandblue text-white border-brandblue"
                : "bg-white text-slate border-brandborder hover:text-navy"
            }`}
          >
            {channel}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate mr-1">
          Follow-ups
        </span>

        {[
          { value: "all_leads", label: "All leads" },
          { value: "open_followups", label: `Active leads (${openFollowUps})` },
          { value: "due_today", label: `Due today (${dueTodayFollowUps})` },
          { value: "overdue", label: `Overdue (${overdueFollowUps})` },
          { value: "no_action", label: `No action set (${noActionSet})` },
        ].map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setFollowUpFilter(filter.value)}
            className={`text-sm px-3 py-2 rounded-sm border transition-colors ${
              followUpFilter === filter.value
                ? "bg-brandblue text-white border-brandblue"
                : "bg-white text-slate border-brandborder hover:text-navy"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate mr-1">
            Proposal
          </span>

          <select
            value={proposalStatusFilter}
            onChange={(e) => setProposalStatusFilter(e.target.value)}
            className="border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
            data-testid="proposal-status-filter"
          >
            <option value="all">All proposals</option>
            <option value="none">No proposal</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
            <option value="superseded">Superseded</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate mr-1">
            Project
          </span>

          <select
            value={projectStatusFilter}
            onChange={(e) => setProjectStatusFilter(e.target.value)}
            className="border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
            data-testid="project-status-filter"
          >
            <option value="all">All projects</option>
            <option value="none">No project</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => {
            setStatusFilter("all");
            setChannelFilter("all");
            setFollowUpFilter("all_leads");
            setProposalStatusFilter("all");
            setProjectStatusFilter("all");
            setSearchQuery("");
          }}
          className="text-sm px-3 py-2 rounded-sm border border-brandborder bg-white text-slate hover:text-navy hover:border-brandblue transition-colors"
          data-testid="clear-all-filters"
        >
          Clear all filters
        </button>
      </div>

      <div className="mb-4">
        <label
          htmlFor="crm-search"
          className="block text-xs font-semibold uppercase tracking-wide text-slate mb-2"
        >
          Search CRM
        </label>

        <div className="relative max-w-xl">
          <Search
            size={17}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate"
          />
          <input
            id="crm-search"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reference, name, company, email, phone or enquiry..."
            className="w-full border border-brandborder bg-white rounded-sm pl-10 pr-4 py-2.5 text-sm text-navy outline-none focus:border-brandblue focus:ring-2 focus:ring-brandblue/20"
            data-testid="crm-search"
          />
        </div>

        {normalizedSearch && (
          <div className="text-xs text-slate mt-2">
            {filteredLeads.length} matching record
            {filteredLeads.length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div className="bg-white border border-brandborder rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center text-slate" data-testid="admin-empty">
            No leads yet.
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-10 text-center text-slate">
            No CRM records match the current search or filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-lightbg text-slate text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Master Ref</th>
                  <th className="text-left px-5 py-3 font-semibold">Name</th>
                  <th className="text-left px-5 py-3 font-semibold">Company</th>
                  <th className="text-left px-5 py-3 font-semibold">Lead Channel</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Proposal</th>
                  <th className="text-left px-5 py-3 font-semibold">Project</th>
                  <th className="text-left px-5 py-3 font-semibold">Value</th>
                  <th className="text-left px-5 py-3 font-semibold">Next action</th>
                  <th className="text-left px-5 py-3 font-semibold hidden lg:table-cell">Received</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => {
                  const followUpState = getFollowUpState(l);
                  const hasNewUpdate = hasNewEnquiryUpdate(l);

                  return (
                  <tr
                    key={l.id}
                    className={`border-t border-brandborder cursor-pointer ${
                      followUpState === "overdue"
                        ? "bg-destructive/5 hover:bg-destructive/10"
                        : followUpState === "due_today"
                          ? "bg-iceblue/40 hover:bg-iceblue/70"
                          : "hover:bg-lightbg"
                    }`}
                    onClick={() => openLead(l)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className="font-mono text-xs font-semibold text-brandblue whitespace-nowrap">
                          {l.master_ref || "Legacy / unassigned"}
                        </span>

                        {hasNewUpdate && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brandblue bg-iceblue px-2 py-0.5 rounded-sm whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-brandblue" />
                            New update
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 font-medium text-navy">{l.name}<div className="text-xs text-slate">{l.email}</div></td>
                    <td className="px-5 py-3 text-slate">{l.company || "—"}</td>
                    <td className="px-5 py-3 text-slate">
                      {l.lead_channel || "Unclassified"}
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs bg-iceblue text-brandblue font-semibold px-2 py-1 rounded-sm">
                        {PIPELINE_STATUSES.find((s) => s.value === (l.status || "new"))?.label || "New"}
                      </span>
                    </td>

                    <td className="px-5 py-3 text-slate">
                      {l.latest_proposal_status ? (
                        <span className="text-xs font-semibold capitalize whitespace-nowrap">
                          {l.latest_proposal_status.replace(/_/g, " ")}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-5 py-3 text-slate">
                      {l.project_status ? (
                        <span className="text-xs font-semibold capitalize whitespace-nowrap">
                          {l.project_status.replace(/_/g, " ")}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-5 py-3 text-slate">
                      {l.estimated_value != null
                        ? `£${Number(l.estimated_value).toLocaleString("en-GB")}`
                        : "—"}
                    </td>
                    <td className="px-5 py-3 text-slate">
                      <div>{l.next_action || "—"}</div>
                      {l.next_action_at && (
                        <>
                          <div className="text-xs mt-1">
                            {new Date(l.next_action_at).toLocaleString()}
                          </div>

                          {followUpState === "overdue" && (
                            <div className="text-xs font-semibold text-destructive mt-1">
                              Overdue
                            </div>
                          )}

                          {followUpState === "due_today" && (
                            <div className="text-xs font-semibold text-brandblue mt-1">
                              Due today
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate hidden lg:table-cell">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {canEdit && (
                        <button onClick={(e) => { e.stopPropagation(); del(l.id); }} data-testid={`lead-delete-${l.id}`} className="text-slate hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNewOpportunity && canEdit && (
        <div
          className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm grid place-items-center p-5"
          onClick={() => setShowNewOpportunity(false)}
          data-testid="new-opportunity-modal"
        >
          <div
            className="bg-white rounded-md max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold text-2xl text-navy">
                  New Opportunity
                </h3>
                <p className="text-sm text-slate mt-1">
                  Create a manually entered CRM opportunity. A permanent Master Reference will be assigned automatically.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Contact name *
                </label>
                <input
                  value={newOpportunity.name}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Company
                </label>
                <input
                  value={newOpportunity.company}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      company: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={newOpportunity.email}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      email: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Telephone
                </label>
                <input
                  type="tel"
                  value={newOpportunity.phone}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      phone: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Job title
                </label>
                <input
                  value={newOpportunity.job_title}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      job_title: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Company size
                </label>
                <select
                  value={newOpportunity.company_size}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      company_size: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  <option value="">Not set</option>
                  {COMPANY_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Opportunity type
                </label>
                <select
                  value={newOpportunity.enquiry_type}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      enquiry_type: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  {MANUAL_ENQUIRY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Lead source
                </label>
                <select
                  value={newOpportunity.lead_source}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      lead_source: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  <option value="">Not set</option>
                  {MANUAL_LEAD_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Service / query interest
                </label>
                <select
                  value={newOpportunity.service_interest}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      service_interest: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  <option value="">Not set</option>
                  {CRM_SERVICE_INTERESTS.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Package interest
                </label>
                <select
                  value={newOpportunity.package_interest}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      package_interest: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  <option value="">None / not set</option>
                  {PACKAGE_INTERESTS.map((pkg) => (
                    <option key={pkg} value={pkg}>
                      {pkg}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Preferred contact
                </label>
                <select
                  value={newOpportunity.preferred_contact}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      preferred_contact: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  <option value="">No preference</option>
                  {PREFERRED_CONTACT.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Contact reason
                </label>
                <select
                  value={newOpportunity.contact_reason}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      contact_reason: e.target.value,
                    }))
                  }
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                >
                  <option value="">Not set</option>
                  {CONTACT_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                  Initial notes / enquiry details
                </label>
                <textarea
                  rows="4"
                  value={newOpportunity.message}
                  onChange={(e) =>
                    setNewOpportunity((current) => ({
                      ...current,
                      message: e.target.value,
                    }))
                  }
                  placeholder="Context from the referral, call, event or conversation."
                  className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowNewOpportunity(false);
                  resetNewOpportunity();
                }}
                className="flex-1 border border-brandborder text-navy font-semibold text-sm py-2.5 rounded-sm hover:border-brandblue transition-colors"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={createOpportunity}
                disabled={creatingOpportunity}
                className="flex-1 bg-navy text-white font-semibold text-sm py-2.5 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
              >
                {creatingOpportunity
                  ? "Creating..."
                  : "Create Opportunity"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm grid place-items-center p-5" onClick={() => setSelected(null)} data-testid="lead-detail-modal">
          <div className="bg-white rounded-md max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-2xl text-navy">
                  {selected.name}
                </h3>
                <a
                  href={`mailto:${selected.email}`}
                  className="text-brandblue font-semibold text-sm"
                >
                  {selected.email}
                </a>
              </div>

              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-slate">
                  Master Ref
                </div>
                <div className="font-mono font-bold text-brandblue">
                  {selected.master_ref || "Legacy / unassigned"}
                </div>
              </div>
            </div>

            {!canEdit && (
              <div
                className="mt-6 bg-lightbg border border-brandborder rounded-md p-5"
                data-testid="lead-readonly-details"
              >
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-wide text-slate">
                    Read only
                  </div>
                  <div className="text-sm text-navy mt-1">
                    Full CRM details are visible. Changes are disabled for this account.
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    [
                      "Status",
                      PIPELINE_STATUSES.find(
                        (status) => status.value === (selected.status || "new")
                      )?.label || "New",
                    ],
                    [
                      "Estimated value",
                      selected.estimated_value !== null &&
                      selected.estimated_value !== undefined &&
                      selected.estimated_value !== ""
                        ? formatCurrency(selected.estimated_value)
                        : "Not set",
                    ],
                    ["Company", selected.company || "Not set"],
                    ["Job title", selected.job_title || "Not set"],
                    ["Company size", selected.company_size || "Not set"],
                    ["Phone", selected.phone || "Not set"],
                    ["Address", selected.address || "Not set"],
                    [
                      "Service interest / query topic",
                      selected.service_interest || "Not set",
                    ],
                    ["Package interest", selected.package_interest || "Not set"],
                    ["Contact reason", selected.contact_reason || "Not set"],
                    ["Timing", selected.timing || "Not set"],
                    ["Lead source", selected.lead_source || "Not set"],
                    ["Lead channel", selected.lead_channel || "Not set"],
                    ["Enquiry type", selected.enquiry_type || "Not set"],
                    [
                      "Preferred contact",
                      selected.preferred_contact || "Not set",
                    ],
                    ["Next action", selected.next_action || "Not set"],
                    [
                      "Next action date",
                      selected.next_action_at
                        ? new Date(selected.next_action_at).toLocaleString()
                        : "Not set",
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className={
                        [
                          "Service interest / query topic",
                          "Package interest",
                          "Contact reason",
                          "Preferred contact",
                        ].includes(label)
                          ? "sm:col-span-2"
                          : ""
                      }
                    >
                      <div className="text-xs uppercase tracking-wide text-slate">
                        {label}
                      </div>
                      <div className="mt-1 text-sm text-navy whitespace-pre-wrap break-words">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                <details className="mt-5 border border-brandborder bg-white rounded-sm">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-navy">
                    Additional discovery details
                  </summary>

                  <div className="border-t border-brandborder p-4 grid gap-4">
                    {[
                      ["What prompted contact", selected.prompted],
                      ["Biggest concern", selected.concern],
                      ["Success looks like", selected.success_outcome],
                      ["Deadline / driver", selected.deadline],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <div className="text-xs uppercase tracking-wide text-slate">
                          {label}
                        </div>
                        <div className="mt-1 text-sm text-navy whitespace-pre-wrap break-words">
                          {value || "Not set"}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>

                {(selected.status || "new") === "lost" && (
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-wide text-slate">
                      Lost reason
                    </div>
                    <div className="mt-1 text-sm text-navy whitespace-pre-wrap break-words">
                      {selected.lost_reason || "Not set"}
                    </div>
                  </div>
                )}
              </div>
            )}

            {editing && canEdit && (
              <div className="mt-6 bg-lightbg border border-brandborder rounded-md p-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Status
                    </label>
                    <select
                      value={editing.status}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          status: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                      data-testid="lead-status"
                    >
                      {PIPELINE_STATUSES.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Estimated value (£)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={editing.estimated_value}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          estimated_value: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                      data-testid="lead-estimated-value"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={editing.company}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          company: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Job title
                    </label>
                    <input
                      type="text"
                      value={editing.job_title}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          job_title: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Company size
                    </label>
                    <select
                      value={editing.company_size}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          company_size: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    >
                      <option value="">Not set</option>
                      {editing.company_size &&
                        !COMPANY_SIZES.includes(editing.company_size) && (
                          <option value={editing.company_size}>
                            {editing.company_size}
                          </option>
                        )}
                      {COMPANY_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      value={editing.phone}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          phone: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={editing.address}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          address: e.target.value,
                        }))
                      }
                      placeholder="Optional"
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Service interest / query topic
                    </label>
                    <select
                      value={editing.service_interest}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          service_interest: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                      data-testid="lead-service-interest"
                    >
                      <option value="">Not set</option>
                      {editing.service_interest &&
                        !CRM_SERVICE_INTERESTS.includes(
                          editing.service_interest
                        ) && (
                          <option value={editing.service_interest}>
                            {editing.service_interest}
                          </option>
                        )}
                      {CRM_SERVICE_INTERESTS.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Package interest
                    </label>
                    <select
                      value={editing.package_interest}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          package_interest: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    >
                      <option value="">None / not set</option>
                      {editing.package_interest &&
                        !PACKAGE_INTERESTS.includes(
                          editing.package_interest
                        ) && (
                          <option value={editing.package_interest}>
                            {editing.package_interest}
                          </option>
                        )}
                      {PACKAGE_INTERESTS.map((pkg) => (
                        <option key={pkg} value={pkg}>
                          {pkg}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Contact reason
                    </label>
                    <select
                      value={editing.contact_reason}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          contact_reason: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    >
                      <option value="">Not set</option>
                      {editing.contact_reason &&
                        !CONTACT_REASONS.includes(
                          editing.contact_reason
                        ) && (
                          <option value={editing.contact_reason}>
                            {editing.contact_reason}
                          </option>
                        )}
                      {CONTACT_REASONS.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Timing
                    </label>
                    <select
                      value={editing.timing}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          timing: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    >
                      <option value="">Not set</option>
                      {editing.timing &&
                        !TIMING_OPTIONS.includes(editing.timing) && (
                          <option value={editing.timing}>
                            {editing.timing}
                          </option>
                        )}
                      {TIMING_OPTIONS.map((timing) => (
                        <option key={timing} value={timing}>
                          {timing}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Lead source
                    </label>
                    <select
                      value={editing.lead_source}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          lead_source: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    >
                      <option value="">Not set</option>
                      {editing.lead_source &&
                        !LEAD_SOURCES.includes(editing.lead_source) && (
                          <option value={editing.lead_source}>
                            {editing.lead_source}
                          </option>
                        )}
                      {LEAD_SOURCES.map((source) => (
                        <option key={source} value={source}>
                          {source}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Preferred contact
                    </label>
                    <select
                      value={editing.preferred_contact}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          preferred_contact: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                    >
                      <option value="">No preference set</option>
                      {editing.preferred_contact &&
                        !PREFERRED_CONTACT.includes(
                          editing.preferred_contact
                        ) && (
                          <option value={editing.preferred_contact}>
                            {editing.preferred_contact}
                          </option>
                        )}
                      {PREFERRED_CONTACT.map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>

                  <details className="sm:col-span-2 border border-brandborder bg-white rounded-sm">
                    <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-navy">
                      Additional discovery details
                    </summary>

                    <div className="border-t border-brandborder p-4 grid gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          What prompted contact
                        </label>
                        <textarea
                          rows="2"
                          value={editing.prompted}
                          onChange={(e) =>
                            setEditing((current) => ({
                              ...current,
                              prompted: e.target.value,
                            }))
                          }
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy resize-y"
                        />
                      </div>

                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          Biggest concern
                        </label>
                        <textarea
                          rows="2"
                          value={editing.concern}
                          onChange={(e) =>
                            setEditing((current) => ({
                              ...current,
                              concern: e.target.value,
                            }))
                          }
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy resize-y"
                        />
                      </div>

                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          Success looks like
                        </label>
                        <textarea
                          rows="2"
                          value={editing.success_outcome}
                          onChange={(e) =>
                            setEditing((current) => ({
                              ...current,
                              success_outcome: e.target.value,
                            }))
                          }
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy resize-y"
                        />
                      </div>

                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          Deadline / driver
                        </label>
                        <textarea
                          rows="2"
                          value={editing.deadline}
                          onChange={(e) =>
                            setEditing((current) => ({
                              ...current,
                              deadline: e.target.value,
                            }))
                          }
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy resize-y"
                        />
                      </div>
                    </div>
                  </details>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Next action
                    </label>
                    <input
                      type="text"
                      value={editing.next_action}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          next_action: e.target.value,
                        }))
                      }
                      placeholder="e.g. Send proposal"
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                      data-testid="lead-next-action"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                      Next action date
                    </label>
                    <input
                      type="datetime-local"
                      value={editing.next_action_at}
                      onChange={(e) =>
                        setEditing((current) => ({
                          ...current,
                          next_action_at: e.target.value,
                        }))
                      }
                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                      data-testid="lead-next-action-at"
                    />
                  </div>

                  {editing.status === "lost" && (
                    <div className="sm:col-span-2">
                      <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                        Lost reason
                      </label>
                      <textarea
                        rows="3"
                        value={editing.lost_reason}
                        onChange={(e) =>
                          setEditing((current) => ({
                            ...current,
                            lost_reason: e.target.value,
                          }))
                        }
                        className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy resize-y"
                        data-testid="lead-lost-reason"
                      />
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={saveLead}
                  disabled={saving}
                  className="mt-4 w-full bg-brandblue text-white font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-navy transition-colors disabled:opacity-50"
                  data-testid="lead-save"
                >
                  {saving ? "Saving..." : "Save lead"}
                </button>

                {(selected.next_action || selected.next_action_at) && (
                  <button
                    type="button"
                    onClick={completeNextAction}
                    disabled={completingAction}
                    className="mt-2 w-full border border-brandborder bg-white text-navy font-semibold text-sm px-4 py-2.5 rounded-sm hover:border-brandblue transition-colors disabled:opacity-50"
                    data-testid="lead-complete-next-action"
                  >
                    {completingAction
                      ? "Completing..."
                      : "Complete next action"}
                  </button>
                )}
              </div>
            )}

            <div className="mt-6 border-t border-brandborder pt-5">
              <h4 className="font-heading font-semibold text-navy mb-3">
                Notes
              </h4>

              {canEdit && (
              <>
              <textarea
                rows="3"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note about this lead..."
                className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy resize-y"
                data-testid="lead-note-input"
              />

              <button
                type="button"
                onClick={addNote}
                disabled={savingNote || !newNote.trim()}
                className="mt-2 bg-navy text-white font-semibold text-sm px-4 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
                data-testid="lead-note-add"
              >
                {savingNote ? "Adding..." : "Add note"}
              </button>
              </>
              )}

              <div className="mt-5 space-y-3">
                {(selected.notes || []).length === 0 ? (
                  <div className="text-sm text-slate">
                    No notes yet.
                  </div>
                ) : (
                  [...selected.notes].reverse().map((note, index) => (
                    <div
                      key={`${note.created_at}-${index}`}
                      className="border border-brandborder rounded-sm p-3 bg-lightbg"
                    >
                      <div className="text-sm text-navy whitespace-pre-wrap">
                        {note.note}
                      </div>
                      <div className="text-xs text-slate mt-2">
                        {new Date(note.created_at).toLocaleString()}
                        {note.created_by ? ` \u00b7 ${note.created_by}` : ""}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-6 border-t border-brandborder pt-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <h4 className="font-heading font-semibold text-navy">
                    Commercial journey
                  </h4>
                  <p className="text-xs text-slate mt-1">
                    Permanent references linked to this Master Reference.
                  </p>
                </div>

                <div className="font-mono text-xs font-semibold text-brandblue">
                  {selected.master_ref || "Legacy / unassigned"}
                </div>
              </div>

              {loadingCommercial ? (
                <div className="text-sm text-slate">
                  Loading commercial records...
                </div>
              ) : (
                <>
                  <div className="border border-brandborder rounded-sm p-4 bg-lightbg">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-slate">
                          Proposal
                        </div>
                        <div className="text-sm font-semibold text-navy mt-1">
                          {proposals.length === 0
                            ? "No formal proposal recorded"
                            : `${proposals.length} proposal${
                                proposals.length === 1 ? "" : "s"
                              }`}
                        </div>
                      </div>

                      {selected.latest_proposal_ref && (
                        <div className="font-mono text-xs font-semibold text-brandblue">
                          {selected.latest_proposal_ref}
                        </div>
                      )}
                    </div>

                    {proposals.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {proposals.map((proposal) => (
                          <div
                            key={proposal.id}
                            className="bg-white border border-brandborder rounded-sm p-3"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <div className="font-mono text-xs font-bold text-brandblue">
                                  {proposal.proposal_ref}
                                </div>
                                <div className="text-sm font-semibold text-navy mt-1">
                                  {proposal.title}
                                </div>
                              </div>

                              <div className="text-right">
                                {proposal.value != null && (
                                  <div className="text-sm font-semibold text-navy">
                                    £{Number(proposal.value).toLocaleString("en-GB")}
                                  </div>
                                )}
                                <select
                                  value={proposal.status || "draft"}
                                  onChange={(e) =>
                                    updateProposalStatus(
                                      proposal,
                                      e.target.value
                                    )
                                  }
                                  disabled={!canEdit || updatingProposalId === proposal.id}
                                  className="mt-1 border border-brandborder bg-white rounded-sm px-2 py-1 text-xs text-navy disabled:opacity-50"
                                  aria-label={`Proposal ${proposal.proposal_ref} status`}
                                >
                                  <option value="draft">Draft</option>
                                  <option value="sent">Sent</option>
                                  <option value="accepted">Accepted</option>
                                  <option value="declined">Declined</option>
                                  <option value="superseded">Superseded</option>
                                </select>
                              </div>
                            </div>

                            {proposal.notes && (
                              <div className="text-xs text-slate mt-2 whitespace-pre-wrap">
                                {proposal.notes}
                              </div>
                            )}

                            {(proposal.sent_at ||
                              proposal.accepted_at ||
                              proposal.declined_at ||
                              proposal.superseded_at) && (
                              <div className="text-xs text-slate mt-2">
                                {proposal.accepted_at
                                  ? `Accepted ${new Date(
                                      proposal.accepted_at
                                    ).toLocaleString("en-GB")}`
                                  : proposal.declined_at
                                  ? `Declined ${new Date(
                                      proposal.declined_at
                                    ).toLocaleString("en-GB")}`
                                  : proposal.superseded_at
                                  ? `Superseded ${new Date(
                                      proposal.superseded_at
                                    ).toLocaleString("en-GB")}`
                                  : proposal.sent_at
                                  ? `Sent ${new Date(
                                      proposal.sent_at
                                    ).toLocaleString("en-GB")}`
                                  : ""}
                              </div>
                            )}

                            {proposal.status_history?.length > 0 && (
                              <div className="mt-3 border-t border-brandborder pt-3">
                                <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                  Status history
                                </div>

                                <div className="space-y-2">
                                  {[...proposal.status_history]
                                    .reverse()
                                    .map((event, index) => (
                                      <div
                                        key={`${event.changed_at}-${index}`}
                                        className="text-xs text-slate"
                                      >
                                        <div>
                                          <span className="capitalize">
                                            {(event.from || "draft").replace(/_/g, " ")}
                                          </span>
                                          {" → "}
                                          <span className="font-semibold text-navy capitalize">
                                            {(event.to || "").replace(/_/g, " ")}
                                          </span>
                                        </div>

                                        <div className="mt-0.5">
                                          {event.changed_at
                                            ? new Date(event.changed_at).toLocaleString("en-GB")
                                            : ""}
                                          {event.changed_by
                                            ? ` · ${event.changed_by}`
                                            : ""}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-3 border-t border-brandborder pt-3">
                              <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                Proposal activity
                              </div>

                              {proposal.activity_notes?.length > 0 && (
                                <div className="space-y-2 mb-3">
                                  {[...proposal.activity_notes]
                                    .reverse()
                                    .map((event, index) => (
                                      <div
                                        key={`${event.created_at}-${index}`}
                                        className="text-xs text-slate bg-lightbg border border-brandborder rounded-sm p-2"
                                      >
                                        <div className="whitespace-pre-wrap text-navy">
                                          {event.note}
                                        </div>
                                        <div className="mt-1">
                                          {event.created_at
                                            ? new Date(event.created_at).toLocaleString("en-GB")
                                            : ""}
                                          {event.created_by
                                            ? ` · ${event.created_by}`
                                            : ""}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}

                              <div className="flex gap-2 items-start">
                                <textarea
                                  rows="2"
                                  value={proposalActivity[proposal.id] || ""}
                                  onChange={(e) =>
                                    setProposalActivity((current) => ({
                                      ...current,
                                      [proposal.id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Add proposal activity note"
                                  className="flex-1 border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                />
                                <button
                                  type="button"
                                  onClick={() => addProposalActivity(proposal)}
                                  disabled={
                                    savingActivityKey === `proposal-${proposal.id}` ||
                                    !(proposalActivity[proposal.id] || "").trim()
                                  }
                                  className="bg-navy text-white font-semibold text-xs px-3 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 border-t border-brandborder pt-4 grid gap-3">
                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          Proposal title
                        </label>
                        <input
                          type="text"
                          value={proposalForm.title}
                          onChange={(e) =>
                            setProposalForm((current) => ({
                              ...current,
                              title: e.target.value,
                            }))
                          }
                          placeholder={`Proposal for ${
                            selected.company || selected.name
                          }`}
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                        />
                      </div>

                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          Proposal value (£)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={proposalForm.value}
                          onChange={(e) =>
                            setProposalForm((current) => ({
                              ...current,
                              value: e.target.value,
                            }))
                          }
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                        />
                      </div>

                      <div>
                        <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                          Proposal notes
                        </label>
                        <textarea
                          rows="2"
                          value={proposalForm.notes}
                          onChange={(e) =>
                            setProposalForm((current) => ({
                              ...current,
                              notes: e.target.value,
                            }))
                          }
                          className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={createProposal}
                        disabled={creatingProposal}
                        className="justify-self-start bg-navy text-white font-semibold text-sm px-4 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
                      >
                        {creatingProposal
                          ? "Creating..."
                          : "Create proposal reference"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 border border-brandborder rounded-sm p-4 bg-lightbg">
                    <div className="text-xs uppercase tracking-wide text-slate">
                      Project
                    </div>

                    {projects.length === 0 ? (
                      <div className="text-sm text-slate mt-2">
                        {selected.status === "won"
                          ? "No project record was found."
                          : "A Project Reference is created automatically when this opportunity is saved as Won."}
                      </div>
                    ) : (
                      <div className="mt-3 space-y-3">
                        {projects.map((project) => {
                          const projectInvoices = invoices.filter(
                            (invoice) => invoice.project_id === project.id
                          );

                          return (
                            <div
                              key={project.id}
                              className="bg-white border border-brandborder rounded-sm p-3"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <div className="font-mono text-xs font-bold text-brandblue">
                                    {project.project_ref}
                                  </div>
                                  <div className="text-sm font-semibold text-navy mt-1">
                                    {project.name}
                                  </div>
                                </div>

                                <select
                                  value={project.status || "active"}
                                  onChange={(e) =>
                                    updateProjectStatus(
                                      project,
                                      e.target.value
                                    )
                                  }
                                  disabled={!canEdit || updatingProjectId === project.id}
                                  className="border border-brandborder bg-white rounded-sm px-2 py-1 text-xs text-navy disabled:opacity-50"
                                  aria-label={`Project ${project.project_ref} status`}
                                >
                                  <option value="active">Active</option>
                                  <option value="on_hold">On Hold</option>
                                  <option value="completed">Completed</option>
                                  <option value="cancelled">Cancelled</option>
                                </select>
                              </div>

                              <div className="text-xs text-slate mt-2">
                                Master:{" "}
                                <span className="font-mono">
                                  {project.master_ref || "—"}
                                </span>
                                {project.proposal_ref && (
                                  <>
                                    {" · "}Proposal:{" "}
                                    <span className="font-mono">
                                      {project.proposal_ref}
                                    </span>
                                  </>
                                )}
                              </div>

                              {project.status_history?.length > 0 && (
                                <div className="mt-3 border-t border-brandborder pt-3">
                                  <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                    Status history
                                  </div>

                                  <div className="space-y-2">
                                    {[...project.status_history]
                                      .reverse()
                                      .map((event, index) => (
                                        <div
                                          key={`${event.changed_at}-${index}`}
                                          className="text-xs text-slate"
                                        >
                                          <div>
                                            <span className="capitalize">
                                              {(event.from || "active").replace(/_/g, " ")}
                                            </span>
                                            {" → "}
                                            <span className="font-semibold text-navy capitalize">
                                              {(event.to || "").replace(/_/g, " ")}
                                            </span>
                                          </div>

                                          <div className="mt-0.5">
                                            {event.changed_at
                                              ? new Date(event.changed_at).toLocaleString("en-GB")
                                              : ""}
                                            {event.changed_by
                                              ? ` · ${event.changed_by}`
                                              : ""}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}

                              <div className="mt-3 border-t border-brandborder pt-3">
                                <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                  Project activity
                                </div>

                                {project.activity_notes?.length > 0 && (
                                  <div className="space-y-2 mb-3">
                                    {[...project.activity_notes]
                                      .reverse()
                                      .map((event, index) => (
                                        <div
                                          key={`${event.created_at}-${index}`}
                                          className="text-xs text-slate bg-lightbg border border-brandborder rounded-sm p-2"
                                        >
                                          <div className="whitespace-pre-wrap text-navy">
                                            {event.note}
                                          </div>
                                          <div className="mt-1">
                                            {event.created_at
                                              ? new Date(event.created_at).toLocaleString("en-GB")
                                              : ""}
                                            {event.created_by
                                              ? ` · ${event.created_by}`
                                              : ""}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}

                                <div className="flex gap-2 items-start">
                                  <textarea
                                    rows="2"
                                    value={projectActivity[project.id] || ""}
                                    onChange={(e) =>
                                      setProjectActivity((current) => ({
                                        ...current,
                                        [project.id]: e.target.value,
                                      }))
                                    }
                                    placeholder="Add project activity note"
                                    className="flex-1 border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => addProjectActivity(project)}
                                    disabled={
                                      savingActivityKey === `project-${project.id}` ||
                                      !(projectActivity[project.id] || "").trim()
                                    }
                                    className="bg-navy text-white font-semibold text-xs px-3 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>

                              <div className="mt-4 border-t border-brandborder pt-4">
                                <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                  Invoices
                                </div>

                                {projectInvoices.length === 0 ? (
                                  <div className="text-sm text-slate">
                                    No invoices recorded.
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {projectInvoices.map((invoice) => (
                                      <div
                                        key={invoice.id}
                                        className="border border-brandborder rounded-sm p-3"
                                      >
                                        <div className="flex flex-wrap justify-between gap-2">
                                          <div>
                                            <div className="font-mono text-xs font-bold text-brandblue">
                                              {invoice.invoice_ref}
                                            </div>
                                            {invoice.external_reference && (
                                              <div className="text-xs text-slate mt-1">
                                                Accounting ref:{" "}
                                                {invoice.external_reference}
                                              </div>
                                            )}
                                          </div>

                                          <div className="text-right">
                                            {invoice.amount != null && (
                                              <div className="text-sm font-semibold text-navy">
                                                £{Number(invoice.amount).toLocaleString("en-GB")}
                                              </div>
                                            )}
                                            <select
                                              value={invoice.status || "draft"}
                                              onChange={(e) =>
                                                updateInvoiceStatus(
                                                  invoice,
                                                  e.target.value
                                                )
                                              }
                                              disabled={!canEdit || updatingInvoiceId === invoice.id}
                                              className="mt-1 border border-brandborder bg-white rounded-sm px-2 py-1 text-xs text-navy disabled:opacity-50"
                                              aria-label={`Invoice ${invoice.invoice_ref} status`}
                                            >
                                              <option value="draft">Draft</option>
                                              <option value="issued">Issued</option>
                                              <option value="paid">Paid</option>
                                              <option value="void">Void</option>
                                            </select>
                                          </div>
                                        </div>
                                          {invoice.status_history?.length > 0 && (
                                            <div className="mt-3 border-t border-brandborder pt-3 text-left">
                                              <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                                Status history
                                              </div>

                                              <div className="space-y-2">
                                                {[...invoice.status_history]
                                                  .reverse()
                                                  .map((event, index) => (
                                                    <div
                                                      key={`${event.changed_at}-${index}`}
                                                      className="text-xs text-slate"
                                                    >
                                                      <div>
                                                        <span className="capitalize">
                                                          {(event.from || "draft").replace(/_/g, " ")}
                                                        </span>
                                                        {" → "}
                                                        <span className="font-semibold text-navy capitalize">
                                                          {(event.to || "").replace(/_/g, " ")}
                                                        </span>
                                                      </div>

                                                      <div className="mt-0.5">
                                                        {event.changed_at
                                                          ? new Date(event.changed_at).toLocaleString("en-GB")
                                                          : ""}
                                                        {event.changed_by
                                                          ? ` · ${event.changed_by}`
                                                          : ""}
                                                      </div>
                                                    </div>
                                                  ))}
                                              </div>
                                            </div>
                                          )}

                                          <div className="mt-3 border-t border-brandborder pt-3 text-left">
                                            <div className="text-xs uppercase tracking-wide text-slate mb-2">
                                              Invoice activity
                                            </div>

                                            {invoice.activity_notes?.length > 0 && (
                                              <div className="space-y-2 mb-3">
                                                {[...invoice.activity_notes]
                                                  .reverse()
                                                  .map((event, index) => (
                                                    <div
                                                      key={`${event.created_at}-${index}`}
                                                      className="text-xs text-slate bg-lightbg border border-brandborder rounded-sm p-2"
                                                    >
                                                      <div className="whitespace-pre-wrap text-navy">
                                                        {event.note}
                                                      </div>
                                                      <div className="mt-1">
                                                        {event.created_at
                                                          ? new Date(event.created_at).toLocaleString("en-GB")
                                                          : ""}
                                                        {event.created_by
                                                          ? ` · ${event.created_by}`
                                                          : ""}
                                                      </div>
                                                    </div>
                                                  ))}
                                              </div>
                                            )}

                                            <div className="flex gap-2 items-start">
                                              <textarea
                                                rows="2"
                                                value={invoiceActivity[invoice.id] || ""}
                                                onChange={(e) =>
                                                  setInvoiceActivity((current) => ({
                                                    ...current,
                                                    [invoice.id]: e.target.value,
                                                  }))
                                                }
                                                placeholder="Add invoice activity note"
                                                className="flex-1 border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => addInvoiceActivity(invoice)}
                                                disabled={
                                                  savingActivityKey === `invoice-${invoice.id}` ||
                                                  !(invoiceActivity[invoice.id] || "").trim()
                                                }
                                                className="bg-navy text-white font-semibold text-xs px-3 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
                                              >
                                                Add
                                              </button>
                                            </div>
                                          </div>

                                          {(invoice.issued_at ||
                                            invoice.paid_at ||
                                            invoice.voided_at) && (
                                            <div className="text-xs text-slate mt-2">
                                              {invoice.paid_at
                                                ? `Paid ${new Date(
                                                    invoice.paid_at
                                                  ).toLocaleString("en-GB")}`
                                                : invoice.voided_at
                                                ? `Voided ${new Date(
                                                    invoice.voided_at
                                                  ).toLocaleString("en-GB")}`
                                                : invoice.issued_at
                                                ? `Issued ${new Date(
                                                    invoice.issued_at
                                                  ).toLocaleString("en-GB")}`
                                                : ""}
                                            </div>
                                          )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="mt-4 border-t border-brandborder pt-4 grid gap-3">
                                  <div className="text-xs text-slate">
                                    Record an invoice here only after the invoice exists in the accounting system. The accounting platform remains the source of truth.
                                  </div>

                                  <div>
                                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                                      Invoice amount (£)
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={invoiceForm.amount}
                                      onChange={(e) =>
                                        setInvoiceForm((current) => ({
                                          ...current,
                                          amount: e.target.value,
                                        }))
                                      }
                                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                                      Accounting system reference
                                    </label>
                                    <input
                                      type="text"
                                      value={invoiceForm.external_reference}
                                      onChange={(e) =>
                                        setInvoiceForm((current) => ({
                                          ...current,
                                          external_reference: e.target.value,
                                        }))
                                      }
                                      placeholder="Optional external invoice number"
                                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                                      Invoice status
                                    </label>
                                    <select
                                      value={invoiceForm.status}
                                      onChange={(e) =>
                                        setInvoiceForm((current) => ({
                                          ...current,
                                          status: e.target.value,
                                        }))
                                      }
                                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                    >
                                      <option value="draft">Draft</option>
                                      <option value="issued">Issued</option>
                                      <option value="paid">Paid</option>
                                      <option value="void">Void</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs uppercase tracking-wide text-slate mb-1">
                                      Invoice notes
                                    </label>
                                    <textarea
                                      rows="2"
                                      value={invoiceForm.notes}
                                      onChange={(e) =>
                                        setInvoiceForm((current) => ({
                                          ...current,
                                          notes: e.target.value,
                                        }))
                                      }
                                      className="w-full border border-brandborder bg-white rounded-sm px-3 py-2 text-sm text-navy"
                                    />
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => createInvoice(project)}
                                    disabled={creatingInvoice}
                                    className="justify-self-start bg-navy text-white font-semibold text-sm px-4 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50"
                                  >
                                    {creatingInvoice
                                      ? "Recording..."
                                      : "Record invoice"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 text-xs text-slate">
                    Credit note numbering is reserved as{" "}
                    <span className="font-mono font-semibold">
                      TR-CN-####
                    </span>
                    . No number is allocated until a credit-note workflow exists.
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 border-t border-brandborder pt-5">
              <h4 className="font-heading font-semibold text-navy mb-3">
                Status history
              </h4>

              {(selected.status_history || []).length === 0 ? (
                <div className="text-sm text-slate">
                  No status changes yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {[...selected.status_history].reverse().map((event, index) => {
                    const fromLabel =
                      PIPELINE_STATUSES.find(
                        (status) => status.value === event.from_status
                      )?.label || event.from_status;

                    const toLabel =
                      PIPELINE_STATUSES.find(
                        (status) => status.value === event.to_status
                      )?.label || event.to_status;

                    return (
                      <div
                        key={`${event.changed_at}-${index}`}
                        className="border border-brandborder rounded-sm p-3 bg-lightbg"
                      >
                        <div className="text-sm text-navy font-medium">
                          {fromLabel} {"\u2192"} {toLabel}
                        </div>
                        <div className="text-xs text-slate mt-1">
                          {new Date(event.changed_at).toLocaleString()}
                          {event.changed_by
                            ? ` \u00b7 ${event.changed_by}`
                            : ""}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-brandborder pt-5">
              <h4 className="font-heading font-semibold text-navy mb-3">
                Enquiry history
              </h4>

              {(selected.enquiry_history || []).length === 0 ? (
                <div className="text-sm text-slate">
                  No structured enquiry history is available for this legacy record.
                </div>
              ) : (
                <div className="space-y-3">
                  {[...(selected.enquiry_history || [])]
                    .sort(
                      (a, b) =>
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime()
                    )
                    .map((event, index) => (
                      <div
                        key={event.event_id || `${event.created_at}-${index}`}
                        className="border border-brandborder rounded-sm p-4 bg-lightbg"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="font-semibold text-navy">
                              {event.enquiry_type ||
                                event.lead_channel ||
                                "Enquiry"}
                            </div>

                            <div className="text-xs text-slate mt-1">
                              {[
                                event.lead_channel,
                                event.lead_source,
                                event.service_interest,
                                event.package_interest,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "No additional classification"}
                            </div>
                          </div>

                          <div className="text-xs text-slate whitespace-nowrap">
                            {event.created_at
                              ? new Date(event.created_at).toLocaleString()
                              : "—"}
                          </div>
                        </div>

                        {event.contact_reason && (
                          <div className="mt-3 text-sm">
                            <span className="font-semibold text-navy">
                              Reason:
                            </span>{" "}
                            <span className="text-slate">
                              {event.contact_reason}
                            </span>
                          </div>
                        )}

                        {event.message && (
                          <div className="mt-3 text-sm text-navy whitespace-pre-wrap">
                            {event.message}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-brandborder pt-5">
              <h4 className="font-heading font-semibold text-navy mb-3">
                Customer message
              </h4>

              {selected.message ? (
                <div className="text-sm text-navy bg-lightbg border border-brandborder p-4 rounded-sm whitespace-pre-wrap">
                  {selected.message}
                </div>
              ) : (
                <div className="text-sm text-slate">
                  No customer message was supplied.
                </div>
              )}
            </div>

            {selected.session_id && (
              <div className="mt-6 border-t border-brandborder pt-5">
                <h4 className="font-heading font-semibold text-navy mb-3">
                  Ray conversation
                </h4>

                {loadingRayConversation ? (
                  <div className="text-sm text-slate">
                    Loading conversation...
                  </div>
                ) : rayConversationError ? (
                  <div className="text-sm text-destructive">
                    {rayConversationError}
                  </div>
                ) : rayConversation.length === 0 ? (
                  <div className="text-sm text-slate">
                    No saved conversation was found.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rayConversation.map((message, index) => (
                      <div
                        key={`${message.created_at || "message"}-${index}`}
                        className="border border-brandborder rounded-sm p-3 bg-lightbg"
                      >
                        <div className="text-xs uppercase tracking-wide text-slate mb-1">
                          {message.role === "user" ? "Visitor" : "Ray"}
                        </div>

                        <div className="text-sm text-navy whitespace-pre-wrap">
                          {message.content}
                        </div>

                        {message.created_at && (
                          <div className="text-xs text-slate mt-2">
                            {new Date(message.created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 border-t border-brandborder pt-5">
              <h4 className="font-heading font-semibold text-navy mb-3">
                Record details
              </h4>

              <div className="grid grid-cols-2 gap-4 text-sm">
                {[
                  ["Address", selected.address],
                  ["Lead channel", selected.lead_channel],
                  ["Enquiry type", selected.enquiry_type],
                  ["Marketing source", selected.lead_source],
                  ["Internal source", selected.source || "website"],
                  ["Landing page", selected.landing_page],
                  ["Referrer", selected.referrer],
                  ["UTM source", selected.utm_source],
                  ["UTM medium", selected.utm_medium],
                  ["UTM campaign", selected.utm_campaign],
                  ["UTM content", selected.utm_content],
                  ["Marketing opt-in", selected.marketing_opt_in ? "Yes" : "No"],
                  ["Privacy consent", selected.privacy_ack ? "Yes" : "No"],
                  ["Received", new Date(selected.created_at).toLocaleString()],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs uppercase tracking-wide text-slate">
                      {k}
                    </div>
                    <div className="text-navy font-medium">
                      {v || "\u2014"}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={() => setSelected(null)} className="flex-1 border border-brandborder text-navy font-semibold text-sm py-2.5 rounded-sm hover:border-brandblue transition-colors" data-testid="lead-modal-close">Close</button>
              <button onClick={() => del(selected.id)} className="inline-flex items-center justify-center gap-2 border border-destructive/40 text-destructive font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-destructive/5 transition-colors" data-testid="lead-modal-delete"><Trash2 size={15} /> Delete (erasure)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
